import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  Users,
  UserCheck,
  Copy,
  Sparkles
} from 'lucide-react';
import { format, addDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GroupTaskModal = ({ open, onClose, onTaskCreated, preSelectedGroup = null }) => {
  const { token, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    due_date: addDays(new Date(), 7),
    group_id: '',
    assignment_type: 'bulk_individual',
    primary_assignee: ''
  });

  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch groups
        const groupsResponse = await fetch(`${API}/groups`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
        
        // Fetch all users for primary assignee selection
        const usersResponse = await fetch(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          setAllUsers(usersData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (open) {
      fetchData();
      // Reset form
      setFormData({
        name: '',
        description: '',
        due_date: addDays(new Date(), 7),
        group_id: preSelectedGroup?.id || '',
        assignment_type: 'bulk_individual',
        primary_assignee: ''
      });
      
      if (preSelectedGroup) {
        setSelectedGroupMembers(preSelectedGroup.members || []);
      } else {
        setSelectedGroupMembers([]);
      }
    }
  }, [open, token, preSelectedGroup]);

  // Update members when group selection changes
  useEffect(() => {
    if (formData.group_id) {
      const group = groups.find(g => g.id === formData.group_id);
      if (group) {
        setSelectedGroupMembers(group.members || []);
        // Reset primary assignee if not in new group
        if (formData.primary_assignee && !group.members.find(m => m.user_id === formData.primary_assignee)) {
          setFormData(prev => ({ ...prev, primary_assignee: '' }));
        }
      }
    } else {
      setSelectedGroupMembers([]);
    }
  }, [formData.group_id, groups]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Task name is required');
      return;
    }
    if (!formData.group_id) {
      toast.error('Please select a group');
      return;
    }
    if (formData.assignment_type === 'collaborative' && !formData.primary_assignee) {
      toast.error('Please select a primary assignee for collaborative task');
      return;
    }
    if (selectedGroupMembers.length === 0) {
      toast.error('Selected group has no members');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/groups/${formData.group_id}/assign-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          group_id: formData.group_id,
          name: formData.name,
          description: formData.description,
          due_date: format(formData.due_date, 'yyyy-MM-dd'),
          assignment_type: formData.assignment_type,
          primary_assignee: formData.assignment_type === 'collaborative' ? formData.primary_assignee : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (formData.assignment_type === 'bulk_individual') {
          toast.success(`Created ${result.tasks_created?.length || 0} individual tasks for group members`);
        } else {
          toast.success('Created collaborative task for the group');
        }
        onTaskCreated();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign task to group');
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Failed to assign task to group');
    } finally {
      setLoading(false);
    }
  };

  const selectedGroup = groups.find(g => g.id === formData.group_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Assign Task to Group
          </DialogTitle>
          <DialogDescription>
            Create tasks for all members of a group at once
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Task Name */}
          <div className="space-y-2">
            <Label htmlFor="group-task-name">Task Name *</Label>
            <Input
              id="group-task-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter task name"
              required
              data-testid="group-task-name-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="group-task-description">Description (Optional)</Label>
            <Textarea
              id="group-task-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add task details..."
              rows={2}
              data-testid="group-task-description-input"
            />
          </div>

          {/* Due Date and Group Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="group-task-due-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.due_date ? format(formData.due_date, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.due_date}
                    onSelect={(date) => {
                      setFormData({ ...formData, due_date: date });
                      setDateOpen(false);
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Select Group *</Label>
              <Select
                value={formData.group_id}
                onValueChange={(value) => setFormData({ ...formData, group_id: value, primary_assignee: '' })}
              >
                <SelectTrigger data-testid="group-select">
                  <SelectValue placeholder="Choose a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No groups available
                    </div>
                  ) : (
                    groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{group.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {group.member_count} members
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Show group members */}
          {selectedGroup && selectedGroupMembers.length > 0 && (
            <div className="p-3 rounded-lg bg-muted/50 border">
              <p className="text-sm font-medium mb-2">Group Members ({selectedGroupMembers.length}):</p>
              <div className="flex flex-wrap gap-2">
                {selectedGroupMembers.map((member) => (
                  <Badge key={member.user_id} variant="secondary">
                    {member.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Assignment Type */}
          <div className="space-y-3">
            <Label>Assignment Type *</Label>
            <RadioGroup
              value={formData.assignment_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, assignment_type: value, primary_assignee: '' }))}
              className="space-y-3"
            >
              <label 
                htmlFor="bulk_individual"
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.assignment_type === 'bulk_individual' 
                    ? 'bg-primary/10 border-primary' 
                    : 'hover:bg-muted/50'
                }`}
              >
                <RadioGroupItem value="bulk_individual" id="bulk_individual" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Copy className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">Individual Tasks (Bulk)</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Creates a <strong>separate task</strong> for each group member. Each member is the primary assignee of their own task. 
                    You can view all tasks together as a "Linked Task Group".
                  </p>
                </div>
              </label>

              <label 
                htmlFor="collaborative"
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.assignment_type === 'collaborative' 
                    ? 'bg-primary/10 border-primary' 
                    : 'hover:bg-muted/50'
                }`}
              >
                <RadioGroupItem value="collaborative" id="collaborative" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-emerald-500" />
                    <span className="font-medium">Collaborative Task</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Creates <strong>one shared task</strong>. Select one primary assignee, all other members become secondary collaborators.
                    Everyone works together on the same task.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Primary Assignee (for collaborative) */}
          {formData.assignment_type === 'collaborative' && (
            <div className="space-y-2">
              <Label>Primary Assignee *</Label>
              <Select
                value={formData.primary_assignee}
                onValueChange={(value) => setFormData(prev => ({ ...prev, primary_assignee: value }))}
              >
                <SelectTrigger data-testid="primary-assignee-select">
                  <SelectValue placeholder="Select primary assignee" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} {u.id === user?.id ? '(You)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedGroupMembers.length > 0 
                  ? `Group members (${selectedGroupMembers.length}) will be added as secondary participants`
                  : 'Select a group to add secondary participants'}
              </p>
            </div>
          )}

          {/* AI Notice */}
          <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <div className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
              <Sparkles className="w-4 h-4" />
              <span>AI will automatically prioritize tasks based on content and due date</span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-group-task-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-group-task-btn">
              {loading ? 'Creating...' : formData.assignment_type === 'bulk_individual' 
                ? `Create ${selectedGroupMembers.length} Task${selectedGroupMembers.length !== 1 ? 's' : ''}`
                : 'Create Collaborative Task'
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GroupTaskModal;
